import {
  UserIntegrationAccessIntegration,
  useGetAgencyInformationForUser,
  useInvalidateUseGetAgencyInformationForUser,
} from '@urbanx/agx.agencies.client';
import { useGetDocuSignAuthorizationUrl } from '@urbanx/agx.docusign.client';
import { useGetRpDataAuthorizationUrl } from '@urbanx/agx.rpdata.client';
import {
  AgxButton,
  AgxSpacer,
  AgxTypography,
  AutopilotIconBlackWhiteLogo,
  AutopilotWordmarkBlackWhiteLogo,
  Breakpoints,
  useFirstPaintLoader,
  useWindowSize,
} from '@urbanx/agx.ui-components2';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConnectionCard from './components/ConnectionCard';
import ContactSupport from './components/ContactSupport';
import IntegrationError, { isIntegrationErrorCode } from './components/IntegrationError';
import './OnboardingPage.scss';

const OnboardingPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const breakpoint = useWindowSize();
  const isMobile = breakpoint === Breakpoints.Mobile;
  const {
    data: agencyInformation,
    isPending,
    isError: agencyInformationError,
  } = useGetAgencyInformationForUser();
  const invalidateAgencyInformation = useInvalidateUseGetAgencyInformationForUser();
  const hasAgencyInformation = !!agencyInformation;
  const {
    data: docuSignAuthorization,
    isPending: docuSignAuthorizationPending,
    isError: docuSignAuthorizationError,
  } = useGetDocuSignAuthorizationUrl(hasAgencyInformation);
  const {
    data: rpDataAuthorization,
    isPending: rpDataAuthorizationPending,
    isError: rpDataAuthorizationError,
  } = useGetRpDataAuthorizationUrl(hasAgencyInformation);
  const docuSignErrorCode = searchParams.get('docuSignErrorCode');
  const rpDataErrorCode = searchParams.get('rpDataErrorCode');
  const initialLoadPending =
    isPending ||
    (hasAgencyInformation && (docuSignAuthorizationPending || rpDataAuthorizationPending));

  useFirstPaintLoader({
    isInitialLoadComplete: !initialLoadPending,
    settledDelayMs: 0,
  });

  if (isPending) return null;

  if (agencyInformationError || !agencyInformation) {
    return (
      <AgxSpacer
        id="onboarding-page"
        direction="vertical"
        contentAlignment="center"
        contentJustification="center"
        padding={
          isMobile ? 'xl' : { top: 'spacing-3xl', bottom: 'spacing-3xl', left: '2xl', right: '2xl' }
        }
        className="onboarding-page"
      >
        <AgxSpacer
          direction="vertical"
          gap="spacing-xl"
          padding={isMobile ? '3xl' : 'spacing-5xl'}
          contentAlignment="center"
          className="onboarding-page__surface onboarding-page__surface--error"
        >
          <AgxTypography
            id="onboarding-agency-information-error"
            type="body_ui"
            size="medium"
            textAlign="center"
            color="var(--text-tertiary, #94a3b8)"
          >
            Unable to load your agency information.
          </AgxTypography>
          <AgxButton
            id="retry-onboarding-agency-information"
            text="Try again"
            variant="primary"
            onClick={() => void invalidateAgencyInformation()}
          />
          <ContactSupport id="onboarding-agency-information" />
        </AgxSpacer>
      </AgxSpacer>
    );
  }

  const docuSign = agencyInformation.userIntegrations?.find(
    ({ integration }) => integration === UserIntegrationAccessIntegration.DocuSign
  );
  const rpData = agencyInformation.userIntegrations?.find(
    ({ integration }) => integration === UserIntegrationAccessIntegration.RPData
  );
  const docuSignAuthorizationUnavailable =
    docuSign?.hasAccess !== true &&
    !docuSignAuthorizationPending &&
    (docuSignAuthorizationError || !docuSignAuthorization?.authorizationUrl);
  const rpDataAuthorizationUnavailable =
    rpData?.hasAccess !== true &&
    !rpDataAuthorizationPending &&
    (rpDataAuthorizationError || !rpDataAuthorization?.authorizationUrl);

  return (
    <AgxSpacer
      id="onboarding-page"
      direction="vertical"
      contentAlignment="center"
      contentJustification="start"
      padding={
        isMobile ? 'xl' : { top: 'spacing-3xl', bottom: 'spacing-3xl', left: '2xl', right: '2xl' }
      }
      className="onboarding-page"
    >
      <AgxSpacer
        direction="vertical"
        gap={isMobile ? 'spacing-4xl' : 'spacing-5xl'}
        padding={isMobile ? '3xl' : 'spacing-5xl'}
        className="onboarding-page__surface"
      >
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          contentJustification="space-between"
        >
          <AutopilotIconBlackWhiteLogo
            className="onboarding-page__mark"
            width={46}
            height={46}
            role="img"
            aria-label="Autopilot mark"
          />
          <AutopilotWordmarkBlackWhiteLogo
            className="onboarding-page__wordmark"
            role="img"
            aria-label="Autopilot"
          />
        </AgxSpacer>

        <AgxSpacer
          direction="vertical"
          gap={isMobile ? 'spacing-4xl' : 'spacing-5xl'}
          padding={isMobile ? undefined : { left: 'spacing-5xl', right: 'spacing-5xl' }}
          className="onboarding-page__content"
        >
          <AgxSpacer direction="vertical" gap="spacing-xl" contentAlignment="center">
            <AgxTypography
              id="onboarding-welcome"
              type="display"
              size="large"
              textAlign="center"
              color="var(--text-primary-on-brand, #10171d)"
            >
              {'Welcome to\nAutopilot'}
            </AgxTypography>
            <AgxTypography
              id="onboarding-instructions"
              type="body"
              size="small"
              textAlign="center"
              color="var(--text-secondary, #1e293b)"
            >
              {'Finalise your account set up by connecting your\nDocusign and RP Data accounts.'}
            </AgxTypography>
          </AgxSpacer>

          <AgxSpacer direction="vertical" gap="spacing-3xl">
            <AgxSpacer direction="vertical" gap="spacing-md">
              <ConnectionCard
                name="DocuSign"
                description={
                  'Connect your Docusign account.\nNote: this needs to be the account provided by Autopilot.'
                }
                integration={docuSign}
                authorizationUrl={docuSignAuthorization?.authorizationUrl}
              />
              {isIntegrationErrorCode(docuSignErrorCode) && (
                <IntegrationError errorCode={docuSignErrorCode} id="docusign" name="Docusign" />
              )}
              {docuSignAuthorizationUnavailable && (
                <IntegrationError
                  errorCode="authorization-url-failed"
                  id="docusign-authorization-url"
                  name="Docusign"
                />
              )}
            </AgxSpacer>
            <AgxSpacer direction="vertical" gap="spacing-md">
              <ConnectionCard
                name="RP Data"
                description="Connect your RP Data account."
                integration={rpData}
                authorizationUrl={rpDataAuthorization?.authorizationUrl}
              />
              {isIntegrationErrorCode(rpDataErrorCode) && (
                <IntegrationError errorCode={rpDataErrorCode} id="rpdata" name="RP Data" />
              )}
              {rpDataAuthorizationUnavailable && (
                <IntegrationError
                  errorCode="authorization-url-failed"
                  id="rpdata-authorization-url"
                  name="RP Data"
                />
              )}
            </AgxSpacer>
            <AgxButton
              id="access-autopilot"
              text="Access Autopilot"
              variant="primary"
              size="large"
              stretch
              disabled={!agencyInformation.hasAccess}
              onClick={() => navigate('/')}
            />
          </AgxSpacer>
        </AgxSpacer>

        <ContactSupport id="onboarding" />
      </AgxSpacer>
    </AgxSpacer>
  );
};

export default OnboardingPage;
